/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useEffect, useState } from "react";
import {} from "./queries";
import {
  Table,
  Icon,
  Grid,
  Segment,
  Transition,
  Modal,
  Dimmer,
  Loader,
  Button,
} from "semantic-ui-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import styles from "../MyAccount.module.css";
import {
  abortProtection,
  executeProtection,
  getBuildArtifactsInfo,
  getBuildById,
} from "./apsApi";
import cStyles from "../../Components.module.css";
import detailsTableStyles from "./DetailsTable.module.css";
import { Ico } from "../../Icon/Icon";
import { Data } from "../../../types/MessageDialogParams";
import {
  DateFormatLong,
  STATE_IN_PROGRESS,
  STATE_NO_BUILDS,
  STATE_PENDING_SUPPORT,
  STATE_QUEUE,
} from "../../Authenticator/utils";
import { getBuildTitle } from "./BuildsList";
import { StateIcon } from "../../Icon/StateIcon";

const BUILD_STATE_UPDATE_INTERVAL = 10000;

interface BuildPopupProps {
  build: Data;
  onClose: () => void;
  setMessage: any;
}

const BuildPopup: React.FC<BuildPopupProps> = ({
  build,
  onClose,
  setMessage,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [updatedBuild, setUpdatedBuild] = useState<Data | null>(null);
  const [artifactList, setArtifactList] = useState<any>(null);

  const getSubscriptionType = (subscriptionType: string) => {
    switch (subscriptionType) {
      case "XTD_PLATFORM":
        return "XTD";
      case "COUNTERSPY_PLATFORM":
        return "Counterspy";
      case "APPSHIELD_PLATFORM":
      case "APPSHIELD_STANDALONE":
        return "App Shield";
      default:
        return "Unknown";
    }
  };

  const getBuildArtifactsList = async (build: Data) => {
    if (!build.id) {
      setArtifactList(null);
      return;
    }

    setIsLoading(true);
    try {
      setArtifactList(await getBuildArtifactsInfo(build.id));
    } catch (e) {
      setArtifactList(null);
    } finally {
      setIsLoading(false);
    }
  };

  const isProtectionInProgress = [STATE_QUEUE, STATE_IN_PROGRESS].includes(
    updatedBuild?.state
  );

  const fetchBuild = (build: Data) => {
    getBuildById(build.id)
      .then((build) => {
        setUpdatedBuild(build);
      })
      .catch(() => {
        setUpdatedBuild(null);
      });
  };

  useEffect(() => {
    fetchBuild(build);
    getBuildArtifactsList(build);
  }, []);

  useEffect(() => {
    let fetchBuildsInterval: any;

    if (isProtectionInProgress) {
      fetchBuildsInterval = setInterval(() => {
        if (!document.hidden) {
          fetchBuild(build);
        }
      }, BUILD_STATE_UPDATE_INTERVAL);
    }

    return () => {
      clearInterval(fetchBuildsInterval);
    };
  }, [updatedBuild?.state]);

  const downloadArtifact = async (url: string, index: number) => {
    return fetch(url).then((resp) =>
      resp.blob().then((blob) => {
        return { fileName: getArtifactName(url, index), blob };
      })
    );
  };

  const downloadArtifacts = (urls: string[]) => {
    return Promise.all(urls.map((url, index) => downloadArtifact(url, index)));
  };

  const createArchive = (artifacts: any) => {
    const zip = JSZip();
    artifacts.forEach((artifact: any) => {
      zip.file(artifact.fileName, artifact.blob);
    });
    zip
      .generateAsync({ type: "blob" })
      .then(function (content) {
        saveAs(content, `${build?.id}.zip`);
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        setMessage({
          title: "Create archive",
          message: `Failed to create artifacts archive : ${e.message}`,
        });
      });
  };

  const downloadAll = (urls: string[]) => {
    setIsLoading(true);
    downloadArtifacts(urls)
      .then(createArchive)
      .catch((e) => {
        setIsLoading(false);
        setMessage({
          title: "Download all",
          message: `Failed to download artifacts: ${e.message}`,
        });
      });
  };

  const getArtifactName = (url: string, index: any) => {
    let fileName = `artifact_${index}`;
    try {
      const path = new URL(url).pathname.split("/");
      fileName = path[path.length - 1];
    } catch {
      console.warn("Can't parse URL", url);
    }
    return fileName;
  };

  const getUrlRow = (url: string, index: any) => {
    const fileName = getArtifactName(url, index);

    return (
      <Table.Row key={index}>
        <Table.Cell>
          <a href={url} className={styles.myAccountLink}>
            {fileName}
          </a>
        </Table.Cell>
      </Table.Row>
    );
  };

  const renderDownloadArtifacts = () => {
    if (!artifactList || !artifactList.length) {
      return (
        <Segment basic className={styles.buildPropertiesSegment}>
          {!isLoading && (
            <div style={{ textAlign: "center" }}>
              <Icon
                name="info circle"
                style={{ marginRight: "5px", color: "#0002b3" }}
              />
              Build artifacts not available
            </div>
          )}
        </Segment>
      );
    }

    return (
      <Segment basic className={styles.buildPropertiesSegment}>
        <Table celled>
          <Table.Body>
            <Table.Row key="name">
              <Table.Cell style={{ fontWeight: "700" }}>
                <Icon name="archive" style={{ marginRight: "1rem" }} />
                Build Artifacts
              </Table.Cell>
            </Table.Row>

            {artifactList &&
              artifactList.length > 0 &&
              artifactList.map((url: any, index: any) => getUrlRow(url, index))}
          </Table.Body>
        </Table>

        <span
          className={styles.myAccountLink}
          style={{}}
          onClick={() => {
            downloadAll(artifactList);
          }}
        >
          <Icon name="download" style={{ fontSize: "16px" }} />
          Download All
        </span>
      </Segment>
    );
  };

  const detailsTable = (data: Data) => {
    const availableData = Object.keys(data).filter((key) => data[key]);

    return (
      <table className={detailsTableStyles.detailsTable}>
        <tbody>
          {availableData.map((key, index) => (
            <tr className={detailsTableStyles.row} key={index}>
              <td className={detailsTableStyles.field}>{key}</td>
              <td className={detailsTableStyles.value}>{data[key]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const getBuildDetails = (build: Data) => {
    if (!build.os) {
      return {};
    }
    const date =
      build.updated.toString().length > 10
        ? new Date(build.updated).toLocaleDateString(
            navigator.language,
            DateFormatLong
          )
        : new Date(build.updated * 1000).toLocaleDateString(
            navigator.language,
            DateFormatLong
          );
    const vmxPlatform =
      build.vmxPlatform === undefined
        ? undefined
        : build.vmxPlatform
        ? "Yes"
        : "No";

    const subscriptionType = getSubscriptionType(build.subscriptionType);

    const buildInfo =
      build.os === "android"
        ? {
            platform: `Android ${
              build.filetype ? build.filetype.toUpperCase() : ""
            }`,
            primaryVersionTitle: "Version Code",
            secondaryVersionTitle: "Version Name",
            primaryVersion: build.osInfo?.versionCode,
            secondaryVersion: build.osInfo?.versionName,
            packageIdTitle: "Package ID",
          }
        : {
            platform: "iOS",
            primaryVersionTitle: "Short Bundle versions string",
            secondaryVersionTitle: "Bundle version",
            primaryVersion: build.osInfo?.CFBundleShortVersionString,
            secondaryVersion: build.osInfo?.CFBundleVersion,
            packageIdTitle: "Bundle ID",
          };

    return { ...buildInfo, date, vmxPlatform, subscriptionType };
  };

  const buildState = (build: Data) => {
    if (!build.state) {
      return "-";
    }

    return (
      <div style={{ whiteSpace: "nowrap", display: "flex" }}>
        <StateIcon
          name={build.state || STATE_NO_BUILDS}
          style={{ width: "18px", height: "18px", marginRight: "5px" }}
        />

        <span>
          {getBuildTitle(build.state)}

          {isProtectionInProgress &&
            build.progressData?.progress !== undefined && (
              <span style={{ marginLeft: "0.5rem" }}>
                {build.progressData?.progress}%
              </span>
            )}

          <Loader
            active={isProtectionInProgress}
            size="tiny"
            inline
            style={{ marginLeft: "1rem" }}
          />
        </span>
      </div>
    );
  };

  const buildDetails = () => {
    const buildInfo = updatedBuild || build;
    const buildDetails: Data = getBuildDetails(buildInfo);

    return detailsTable({
      "Build ID": buildInfo.id,
      State: buildState(buildInfo),
      Updated: buildDetails.date,
      Platform: buildDetails.platform,
      "Package ID": buildInfo.applicationPackageId,
      Filename: buildInfo.filename,
      "Mapping Filename": buildInfo.mappingFileName,
      "Build name": buildInfo.label,
      ...(buildInfo.os === "android" && {
        "Target SDK Version": buildInfo.osInfo?.targetSdkVersion,
      }),
      ...(buildInfo.os === "android" && {
        "Minimum SDK Version": buildInfo.osInfo?.minSdkVersion,
      }),
      [buildDetails.primaryVersionTitle]: buildDetails.primaryVersion,
      [buildDetails.secondaryVersionTitle]: buildDetails.secondaryVersion,
      "Created by": buildInfo.creatorEmail,
      Organization: buildInfo.orgName,
      "Verimatrix Platform": buildDetails.vmxPlatform,
      "Protection Type": buildDetails.subscriptionType,
      Warnings: buildInfo.buildWarnings?.join(", "),
      Errors: buildInfo.buildErrors?.join(", "),
    });
  };

  const handleReprotect = () => {
    setIsLoading(true);
    isProtectionInProgress
      ? abortProtection(build)
          .then(() => fetchBuild(build))
          .catch((e) => {
            setMessage({
              title: "Abort Protection",
              message: `Failed to abort protection: ${e.message}`,
            });
          })
          .finally(() => {
            setTimeout(() => {
              setIsLoading(false);
            }, 1000);
          })
      : executeProtection(build)
          .then(() => fetchBuild(build))
          .catch((e) => {
            setMessage({
              title: "Reprotect",
              message: `Failed to execute protection: ${
                e.response?.data?.errorDescription || e.message
              }`,
            });
          })
          .finally(() => {
            setTimeout(() => {
              setIsLoading(false);
            }, 1000);
          });
  };

  const handleFailBuild = () => {
    setIsLoading(true);
    abortProtection(build)
      .then(() => fetchBuild(build))
      .catch((e) => {
        setMessage({
          title: "Fail Build",
          message: `Failed to fail build: ${e.message}`,
        });
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      });
  };

  return (
    <Transition transitionOnMount={true} animation="fade" duration="300">
      <Modal
        size="fullscreen"
        open
        onClose={onClose}
        style={{ minHeight: "20rem" }}
      >
        <Dimmer active={isLoading}>
          <Loader active={isLoading} size="large"></Loader>
        </Dimmer>

        <Grid
          stackable
          columns={3}
          padded
          style={{ padding: "1rem 1rem 2rem 1rem" }}
        >
          <Grid.Row>
            <Grid.Column width={14}>
              <span className={cStyles.title}>Build Properties</span>
            </Grid.Column>

            <Grid.Column width={2} floated="right">
              <div className={cStyles.closeButton} onClick={() => onClose()}>
                <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
              </div>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={{ padding: 0 }}>
            <Grid.Column width={8} style={{ overflow: "auto" }}>
              {buildDetails()}

              {!isLoading && !updatedBuild && (
                <div style={{ padding: "0 1rem 1rem 1rem", color: "#f44336" }}>
                  This build is not available
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  primary={!isProtectionInProgress}
                  secondary={isProtectionInProgress}
                  className={cStyles.buttonPrime}
                  disabled={isLoading || !updatedBuild}
                  onClick={() => {
                    handleReprotect();
                  }}
                >
                  <Ico
                    name={isProtectionInProgress ? "cancel" : "protect"}
                    style={{ marginRight: "10px" }}
                  />
                  {isProtectionInProgress ? "Abort Protection" : "Reprotect"}
                </Button>

                <Button
                  secondary
                  className={cStyles.buttonPrime}
                  disabled={updatedBuild?.state !== STATE_PENDING_SUPPORT}
                  onClick={() => handleFailBuild()}
                >
                  <Ico name="cancel" style={{ marginRight: "10px" }} />
                  Fail Build
                </Button>
              </div>
            </Grid.Column>

            <Grid.Column width={8} floated="right">
              {renderDownloadArtifacts()}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal>
    </Transition>
  );
};

export default BuildPopup;
